import { FormProvider, useForm } from 'react-hook-form';
import FormFieldInput from '../../forms/form-field-input';
import Button from '../../content/button';
import React, { useState } from 'react';
import { useFormat } from '../../../../helpers/hooks/useFormat';
import { useAccount } from '@Provider/frontastic';
import ChevronLeftIcon from 'components/icons/chevron-left';
import ListCheckIcon from 'components/icons/list-check';

type loginData = {
  email: string;
  password?: string;
};

const CheckoutLogin = () => {
  const { loggedIn, login, requestPasswordReset } = useAccount();
  const formMethods = useForm({
    mode: 'onChange',
    shouldUseNativeValidation: true,
  });
  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });
  const { formatMessage: formatErrorMessage } = useFormat({ name: 'error' });
  const { formatMessage: formatCheckoutMessage } = useFormat({ name: 'checkout' });
  const { formatMessage: formatCommonMessage } = useFormat({ name: 'common' });

  const [showLogin, setShowLogin] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [success, setSuccess] = useState('');
  const [resendPasswordReset, setResendPasswordReset] = useState(false);

  const resendPasswordResetForUser = async (email) => {
    try {
      await requestPasswordReset(email);
      setSuccess(
        formatAccountMessage({
          id: 'verification.resent',
          defaultMessage: 'An email was sent to {email}',
          values: { email: email },
        }),
      );
    } catch (err) {
      setLoginError(formatErrorMessage({ id: 'wentWrong', defaultMessage: 'Sorry. Something went wrong..' }));
    }
  };

  const handleSubmit = async (loginData: loginData) => {
    if (resendPasswordReset) {
      await resendPasswordResetForUser(loginData.email);
    } else {
      try {
        const response = await login(loginData.email, loginData.password);
        if (!response.accountId) {
          setLoginError(formatErrorMessage({ id: 'auth.wrong', defaultMessage: 'Wrong email address or password' }));
        } else {
          setLoginSuccess(true);
        }
      } catch (err) {
        setLoginError(formatErrorMessage({ id: 'wentWrong', defaultMessage: 'Sorry. Something went wrong..' }));
      }
    }
  };

  return (
    <>
      {!loggedIn ? (
        <div className="mb-8 flex bg-neutral-50 p-5">
          <div className="w-full lg:w-auto lg:min-w-80">
            {!showLogin ? (
              <div className="flex flex-wrap gap-4">
                <div className="basis-full text-md font-semibold">
                  {formatCheckoutMessage({ id: 'accountQuestion', defaultMessage: 'Already have an account?' })}
                </div>
                <Button style="hollow" onClick={() => setShowLogin(true)} className="basis-full lg:basis-auto">
                  Logge dich hier ein!
                </Button>
                <p className="basis-full text-xs">
                  {formatCheckoutMessage({
                    id: 'signUp.info',
                    defaultMessage: 'Alternatively, you can log in or create an account after completing your order.',
                  })}
                </p>
              </div>
            ) : (
              <FormProvider {...formMethods}>
                {!resendPasswordReset ? (
                  <button className="mb-4 flex cursor-pointer gap-2" onClick={() => setShowLogin(false)}>
                    <ChevronLeftIcon className="size-6" />
                    <span className="text-md font-semibold">Einloggen</span>
                  </button>
                ) : (
                  <button className="mb-4 flex cursor-pointer gap-2" onClick={() => setResendPasswordReset(false)}>
                    <ChevronLeftIcon className="size-6" />
                    <span className="text-md font-semibold">Passwort zurücksetzen</span>
                  </button>
                )}

                <form className="space-y-5" onSubmit={formMethods.handleSubmit(handleSubmit)}>
                  {success && <p className="text-base text-green-600">{success}</p>}
                  {loginError && <p className="text-base text-danger-700">{loginError}</p>}
                  <FormFieldInput
                    name="email"
                    value=""
                    inputType="email"
                    inputAutoComplete="email"
                    placeholder={formatAccountMessage({ id: 'email.enter', defaultMessage: 'Enter your email' })}
                  />
                  {!resendPasswordReset && (
                    <FormFieldInput
                      name="password"
                      value=""
                      inputType="password"
                      inputAutoComplete="new-password"
                      placeholder="&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;"
                      inputClassNames="block w-full appearance-none rounded border border-neutral-600 px-4 py-3.5 text-md placeholder:text-neutral-700 focus:border-neutral-950 focus:outline-none focus:ring-neutral-950"
                    />
                  )}
                  {!resendPasswordReset && (
                    <div className="text-center text-sm lg:text-left">
                      <span
                        className="cursor-pointer text-secondary-600 underline transition hover:text-secondary-600/90"
                        onClick={() => setResendPasswordReset(true)}
                      >
                        {formatAccountMessage({ id: 'password.forgot', defaultMessage: 'Forgot your password?' })}
                      </span>
                    </div>
                  )}
                  <div className="coll-span-full">
                    <Button style="primary" type="submit" className="w-full lg:w-auto">
                      {resendPasswordReset
                        ? formatCommonMessage({ id: 'submit', defaultMessage: 'Submit' })
                        : formatAccountMessage({ id: 'sign.in', defaultMessage: 'Sign in' })}
                    </Button>
                  </div>
                </form>
              </FormProvider>
            )}
          </div>
        </div>
      ) : (
        <>
          {loginSuccess && (
            <div className="mb-6 flex gap-2">
              <ListCheckIcon className="size-6" />
              <span>Du bist jetzt eingeloggt.</span>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default CheckoutLogin;
